"use client";

import { CMSData } from "@/cms/models";
import { ApplicationDataType } from "@/services/queries/get-application-details";
import { GetActorQuery } from "@/utils/gql/graphql";
import Card from "./Card";
import VCard from "vcard-creator";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAddressCard } from "@fortawesome/sharp-light-svg-icons";
import Link from "next/link";

export interface EmployeeComponentProps {
  actorId: string;
  actorDisplayName: string;
  config: {
    aspectRatio?: string;
    nameTextColor?: string;
    nameTextSize?: string;
    positionTextColor?: string;
    positionTextSize?: string;
    contactDetailsTextColor?: string;
    contactDetailsTextSize?: string;
  };
  actor: GetActorQuery["actor"];
}

const EmployeeCardComponent: React.FC<{
  config: EmployeeComponentProps;
  app: ApplicationDataType;
  cmsData: CMSData;
}> = ({ config }) => {
  if (!config.actor) {
    return null;
  }

  return (
    <Card className="bg-[#ecf0f1]">
      <div className="md:h-full flex flex-col">
        <div
          style={
            {
              "--aspect-ratio": config.config.aspectRatio,
            } as React.CSSProperties
          }
          className={`${config.config.aspectRatio ? `aspect-[var(--aspect-ratio)]` : "h-[18rem]"} relative`}
        >
          {config.actor?.image?.mdUrl && (
            <img
              src={config.actor.image?.mdUrl}
              alt="employee image"
              className="object-cover absolute inset-0 w-full h-full"
            />
          )}
        </div>
        <div className="p-4 flex flex-col gap-4 homepage-text-sm flex-1 justify-between">
          <div className="flex gap-4 items-center justify-between">
            <div className="flex flex-col">
              <span
                className={`homepage-text-${config.config.nameTextSize ?? "lg"} leading-4`}
                style={{ color: config.config.nameTextColor }}
              >
                {config.actor?.displayName}
              </span>
              <span
                className={`homepage-text-${config.config.positionTextSize ?? "sm"} leading-4`}
                style={{ color: config.config.positionTextColor }}
              >
                {config.actor?.position}
              </span>
            </div>
            <button
              aria-label="vcard"
              onClick={() => {
                const vCard = new VCard();
                vCard.addName(config.actor!.displayName!);
                if (config.actor!.position) {
                  vCard.addRole(config.actor!.position);
                }
                if (config.actor!.image?.mdUrl) {
                  vCard.addPhotoURL(config.actor!.image?.mdUrl);
                }
                if (config.actor?.email?.address) {
                  vCard.addEmail(config.actor?.email.address);
                }
                if (config.actor?.phone?.internationalNumber) {
                  vCard.addPhoneNumber(
                    config.actor?.phone?.internationalNumber
                  );
                }
                const blob = new Blob([vCard.toString()], {
                  type: "text/vcard",
                });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = `${config.actor?.displayName}.vcf`;
                link.click();
                URL.revokeObjectURL(url);
              }}
            >
              <FontAwesomeIcon className="size-5" icon={faAddressCard} />
            </button>
          </div>
          <div
            className={`flex flex-col homepage-text-${config.config.contactDetailsTextSize ?? "sm"}`}
            style={{ color: config.config.contactDetailsTextColor }}
          >
            {config.actor?.phone?.internationalNumber && (
              <Link href={`tel:${config.actor?.phone?.internationalNumber}`}>
                <span>{config.actor?.phone?.internationalNumber}</span>
              </Link>
            )}

            {config.actor?.email && (
              <Link href={`mailto:${config.actor?.email?.address}`}>
                <span>{config.actor?.email?.address}</span>
              </Link>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default EmployeeCardComponent;
